/**
 * Contact list screen for the Address Book modal.
 * Shows all contacts or contacts filtered by listing category.
 * Equivalent to AddressBook but for use inside IonNav.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonButton,
  IonContent,
  IonIcon,
  IonActionSheet,
  useIonViewWillLeave,
} from '@ionic/react';
import {
  downloadOutline,
  cloudUploadOutline,
  closeOutline,
  add,
} from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import { useIonRouter } from '@ionic/react';
import { useAppToast } from '@/hooks/useAppToast';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import {
  useContactPicker,
  type DeviceContactSelection,
} from '@/contexts/ContactPickerContext';
import { useContactState } from './ContactStateContext';
import { selectAllContacts, selectContactsByListingCategory } from './reducer';
import { Contact } from './types';
import { contactsToVCards, parseVCardFile } from './util';
import { useImportToast } from './useImportToast';
import { BackupReminder } from './backupReminder';
import SearchableContactList from '@/components/SearchableContactList';
import '@/components/Interstitial.css';
import {
  pxToRem,
  footerControlButtonStyle,
  footerControlRoundButtonStyle,
} from '@/modules/config/constants';

const Contacts: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const router = useIonRouter();
  const listingCategory =
    listId === 'all' ? undefined : decodeURIComponent(listId);
  const { state, duplicatesToResolve, dispatch, isLoading } = useContactState();
  const allContacts = selectAllContacts(state);
  const contacts = listingCategory
    ? selectContactsByListingCategory(state, listingCategory)
    : allContacts;

  const [presentToast] = useAppToast();
  const presentImportToast = useImportToast();
  const { isIonModeIOS } = useDeviceInfo();
  const { DeviceContactPicker } = useContactPicker();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const importedCountRef = useRef<number>(0);
  const [pendingImportContacts, setPendingImportContacts] = useState<
    Contact[] | null
  >(null);
  const [showAddToListSheet, setShowAddToListSheet] = useState(false);

  const title = listingCategory ? listingCategory : 'All Contacts';

  const handleExport = () => {
    try {
      const vcardContent = contactsToVCards(contacts);
      if (!vcardContent) {
        presentToast({
          message: 'No contacts to export',
          duration: 2000,
          color: 'warning',
        });
        return;
      }
      const blob = new Blob([vcardContent], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `address-book-${new Date().toISOString().split('T')[0]}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      presentToast({
        message: 'Contacts exported successfully',
        duration: 2000,
        color: 'success',
      });
    } catch {
      presentToast({
        message: 'Failed to export contacts',
        duration: 2000,
        color: 'danger',
      });
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleAddContactClick = () => {
    router.push('/ab/contacts/new', 'forward', 'push');
  };

  const handleContactClick = (contact: Contact) => {
    router.push('/ab/contacts/' + contact.localId, 'forward', 'push');
  };

  const dispatchImport = (importedContacts: Contact[]) => {
    importedCountRef.current = importedContacts.length;
    // The import's toast (shown once any duplicates are settled) carries the
    // backup reminder, so the change itself must not raise one first.
    BackupReminder.markShown();
    dispatch({
      type: 'PROVISIONALLY_ADD_CONTACTS',
      payload: importedContacts,
    });
  };

  // Add freshly-imported contacts to the address book. When viewing a listing
  // category, first ask whether to tag them with that category; otherwise add
  // them straight away. Shared by .vcf import and the device contact picker.
  const ingestImportedContacts = (importedContacts: Contact[]) => {
    if (importedContacts.length === 0) return;
    if (listingCategory) {
      setPendingImportContacts(importedContacts);
      setShowAddToListSheet(true);
    } else {
      dispatchImport(importedContacts);
    }
  };

  // Ingestion handler for device-picked contacts: add them to the address book
  // (via the shared import path) and warn about any skipped entries.
  const handleDeviceContacts = ({
    contacts: picked,
    noSmsableCount,
    droppedPhoneCount,
  }: DeviceContactSelection) => {
    const warnings: string[] = [];
    if (noSmsableCount > 0) {
      warnings.push(
        `Skipped ${noSmsableCount} contact${noSmsableCount > 1 ? 's' : ''} with no SMS-able phone numbers`
      );
    }
    if (droppedPhoneCount > 0) {
      warnings.push(
        `Skipped ${droppedPhoneCount} duplicate phone number${droppedPhoneCount > 1 ? 's' : ''} (a common issue with PWA contact pickers)`
      );
    }

    if (picked.length === 0) {
      presentToast({
        message:
          warnings.length > 0
            ? warnings.join('<br>')
            : 'No valid contacts selected',
        duration: 3000,
        color: 'warning',
      });
      return;
    }

    ingestImportedContacts(picked);
    if (warnings.length > 0) {
      presentToast({
        message: warnings.join('<br>'),
        duration: 3000,
        color: 'warning',
      });
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.vcf')) {
      presentToast({
        message: 'Please select a .vcf file',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    try {
      const importedContacts = await parseVCardFile(file);
      if (importedContacts.length === 0) {
        presentToast({
          message:
            'No valid contacts found in file. Please check the file format.',
          duration: 3000,
          color: 'warning',
        });
        return;
      }
      ingestImportedContacts(importedContacts);
    } catch {
      presentToast({
        message: 'Failed to import contacts. Please check the file format.',
        duration: 3000,
        color: 'danger',
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddToListConfirm = (addToList: boolean) => {
    if (!pendingImportContacts) return;
    if (addToList && listingCategory) {
      const withCategory = pendingImportContacts.map(c => ({
        ...c,
        listingCategories: c.listingCategories.includes(listingCategory)
          ? c.listingCategories
          : [...c.listingCategories, listingCategory],
      }));
      dispatchImport(withCategory);
    } else {
      dispatchImport(pendingImportContacts);
    }
    setPendingImportContacts(null);
  };

  useEffect(() => {
    if (
      importedCountRef.current > 0 &&
      duplicatesToResolve.length === 0 &&
      !isLoading
    ) {
      const count = importedCountRef.current;
      importedCountRef.current = 0;
      presentImportToast(
        count === 1
          ? '1 contact imported successfully'
          : `${count} contacts imported successfully`
      );
    }
  }, [duplicatesToResolve.length, isLoading, presentImportToast]);

  // "Lists" moves up the address book hierarchy, which is not the same as
  // moving back through history: the Snowball menu opens the address book
  // directly on All Contacts, so Lists is usually not on the stack behind us
  // and a history pop would leave the address book altogether. IonBackButton
  // does not type onClick, since it owns the click to run that pop, but its
  // React wrapper spreads caller props over the internal handler — so this
  // replaces the pop while keeping the platform-correct rendering.
  const upToLists = {
    onClick: () => router.push('/ab/lists', 'back', 'push'),
  };

  return (
    <IonPage data-testid="address-book-contacts-view">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              // Also what makes ion-back-button render at all: without a
              // defaultHref it never gets the `show-back-button` class.
              defaultHref="/ab/lists"
              {...upToLists}
              // Ionic picks the icon per mode on its own, but its per-mode
              // default label ("Back" on ios, none on md) only applies when
              // text is omitted. md does not hide a supplied one -- it styles
              // it, uppercased, and the button loses the round icon-only
              // target -- so withhold it there. Keyed to the mode because that
              // is what decides the rendering this pairs with.
              text={isIonModeIOS ? 'Lists' : undefined}
              data-testid="address-book-back-button"
            />
          </IonButtons>
          <IonTitle>{title}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              fill="clear"
              color="medium"
              onClick={() => router.goBack()}
              aria-label="Close"
              data-testid="address-book-close-button"
            >
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent data-testid="address-book-page">
        <div style={{ padding: '20px 0' }}>
          {listingCategory && (
            <h1
              style={{
                fontSize: pxToRem(28),
                fontWeight: 700,
                margin: '0 20px 16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              data-testid="sublist-header"
            >
              {listingCategory}
            </h1>
          )}

          <SearchableContactList
            contacts={contacts}
            isLoading={isLoading}
            onContactClick={handleContactClick}
            actionRow={
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 12,
                  }}
                >
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="secondary"
                    className="footer-glass-button"
                    // Frosted "liquid glass" fill (not-quite opaque + backdrop
                    // blur) so the scrolling list behind the scrim-less footer
                    // dock stays legible behind the outline button instead of
                    // jumbling with its label.
                    style={
                      {
                        margin: 0,
                        ...footerControlButtonStyle,
                      } as React.CSSProperties
                    }
                    onClick={handleExport}
                    disabled={contacts.length === 0}
                  >
                    <IonIcon icon={downloadOutline} slot="start" />
                    Export
                  </IonButton>
                  <IonButton
                    expand="block"
                    fill="outline"
                    color="secondary"
                    className="footer-glass-button"
                    // Frosted fill — see the Export button above.
                    style={
                      {
                        margin: 0,
                        ...footerControlButtonStyle,
                      } as React.CSSProperties
                    }
                    onClick={handleImportClick}
                  >
                    <IonIcon icon={cloudUploadOutline} slot="start" />
                    Import
                  </IonButton>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".vcf"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </>
            }
            footerElemWhenNotSearching={
              <div
                style={{
                  pointerEvents: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flex: '0 0 auto',
                }}
              >
                <IonButton
                  fill="clear"
                  onClick={handleAddContactClick}
                  aria-label="Add contact"
                  data-testid="add-contact-button"
                  className="footer-glass-button footer-glass-round"
                  style={
                    {
                      ...footerControlRoundButtonStyle,
                      width: 44,
                      height: 44,
                      '--border-radius': '50%',
                      flex: '0 0 44px',
                    } as React.CSSProperties
                  }
                >
                  <IonIcon
                    slot="icon-only"
                    icon={add}
                    style={{ fontSize: pxToRem(28) }}
                  />
                </IonButton>
                <DeviceContactPicker onSelect={handleDeviceContacts} />
              </div>
            }
            sparseHint={
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#666',
                }}
              >
                {listingCategory ? (
                  <p>No contacts in this list.</p>
                ) : (
                  <>
                    <p>
                      Current address book status: <strong>Ghost town</strong>
                    </p>
                    <p style={{ marginTop: 8 }}>
                      You can{' '}
                      <button
                        className="interstitial__link"
                        onClick={() =>
                          router.push(
                            '/settings/contacts/import',
                            'forward',
                            'push'
                          )
                        }
                      >
                        import entries
                      </button>{' '}
                      from your Contacts app into your Snowball address book.
                    </p>
                    {contacts.length === 0 && (
                      <p style={{ marginTop: 12 }}>
                        Worried you lost everything? Don&apos;t panic! First try
                        re-verifying your phone number and we&apos;ll
                        automatically restore from a backup if there&apos;s one
                        encrypted on our server.
                      </p>
                    )}
                  </>
                )}
              </div>
            }
          />
        </div>
      </IonContent>

      {/* Sublist import prompt — ActionSheet instead of Alert */}
      <IonActionSheet
        isOpen={showAddToListSheet}
        onDidDismiss={() => setShowAddToListSheet(false)}
        header={`Add to '${listingCategory}'?`}
        buttons={[
          {
            text: `Yes, add to ${listingCategory}`,
            handler: () => handleAddToListConfirm(true),
          },
          {
            text: 'No, just import',
            role: 'cancel',
            handler: () => handleAddToListConfirm(false),
          },
        ]}
      />
    </IonPage>
  );
};

export default Contacts;
